import { getBiddingStrategyPerformanceGoalType } from './helpers'
import { capitalizeWords, formatStringWithUnderscore } from '../../helpers/common'

import { NAME } from './LineItemForms/LineItemDv360Create/LineItemDv360CreateForm/LineItemDV360CreateMainForm/fields'
import { STATUS } from './fields'
import { PERFORMANCE_GOAL_TYPE_CPA } from './CampaignForms/CampaignDv360Create/CampaignDv360CreateForm/fields'
import { formatDateBE } from '../../constants/dates'
import { DISPLAY_DEFAULT, VIDEO_DEFAULT } from './UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import {
  SHOW_COUNTRIES,
  SHOW_OPTION,
  SHOW_OPTION_COUNTRIES,
  SHOW_OPTION_REGIONS,
  SHOW_REGIONS
} from '../ReusableFormFields/GeolocationsFields/fields'
import {
  GENDER_OPTION,
  GENDER_OPTION_ALL,
  GENDER_OPTION_FEMALES,
  GENDER_OPTION_MALES
} from '../ReusableFormFields/GenderFields/fields'
import {
  GENDER_FEMALE,
  GENDER_MALE
} from './LineItemForms/LineItemDv360Create/LineItemDv360CreateForm/LineItemDV360GenderForm/fields'
import { CUSTOM_AGE_RANGES } from '../ReusableFormFields/AgeRangesFields/fields'

import { formatFrequencyToBE } from './formatters'

export const getIsPixelRequired = campaign => {
  return campaign.objective === PERFORMANCE_GOAL_TYPE_CPA
}
export const createDV360LineItemPayload = ({
  name,
  lineItemType,
  status,
  startDate,
  endDate,
  pixels = [],
  campaign = {},
  values = {}
}) => {
  const biddingStrategyPerformanceGoalType =
    lineItemType === VIDEO_DEFAULT
      ? 'BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_AV_VIEWED'
      : getBiddingStrategyPerformanceGoalType(campaign.objective)

  const isPixelRequired = getIsPixelRequired(campaign)

  return {
    [NAME]: name,
    [STATUS]: status,

    line_item_type: lineItemType === VIDEO_DEFAULT ? VIDEO_DEFAULT : DISPLAY_DEFAULT,

    flight: {
      flight_date_type: 'LINE_ITEM_FLIGHT_DATE_TYPE_CUSTOM',
      date_range: {
        start_date: formatDateBE(new Date(startDate)),
        end_date: formatDateBE(new Date(endDate))
      }
    },
    pacing: {
      pacing_period: 'PACING_PERIOD_FLIGHT',
      pacing_type: 'PACING_TYPE_ASAP'
    },
    budget: {
      budget_allocation_type: 'LINE_ITEM_BUDGET_ALLOCATION_TYPE_UNLIMITED',
      budget_unit: 'BUDGET_UNIT_CURRENCY'
    },
    bid_strategy: {
      maximize_spend_auto_bid: {
        performance_goal_type: biddingStrategyPerformanceGoalType
      }
    },
    frequency_cap: formatFrequencyToBE(values),
    partner_revenue_model: {
      markup_type: 'PARTNER_REVENUE_MODEL_MARKUP_TYPE_TOTAL_MEDIA_COST_MARKUP'
    },

    ...(isPixelRequired && {
      conversion_counting: {
        floodlight_activity_configs: pixels.map(pixel => ({
          floodlight_activity_id: pixel.value
        }))
      }
    })
  }
}

export const transformGeolocationsToBE = values => {
  const geolocationsOption = values[SHOW_OPTION]

  const countries = values[SHOW_COUNTRIES]
  const regions = values[SHOW_REGIONS]

  if (geolocationsOption === SHOW_OPTION_COUNTRIES) {
    return countries.map(item => ({
      targeting_option_id: item.resourceName?.split('/')[1]
    }))
  } else if (geolocationsOption === SHOW_OPTION_REGIONS) {
    return regions.map(item => ({
      targeting_option_id: item.value
    }))
  }

  return []
}

export const transformGendersToBE = values => {
  const gendersIds = []

  if (values[GENDER_OPTION] === GENDER_OPTION_ALL) {
    gendersIds.push(GENDER_MALE, GENDER_FEMALE)
  }

  if (values[GENDER_OPTION] === GENDER_OPTION_MALES) {
    gendersIds.push(GENDER_MALE)
  }

  if (values[GENDER_OPTION] === GENDER_OPTION_FEMALES) {
    gendersIds.push(GENDER_FEMALE)
  }

  return gendersIds
}

export const isAgeObjectContinuousRange = ageObject => {
  // Users may select only one age range, so we need to check if the ageObject is a continuous range
  // E.g. { AGE_13_17: true, AGE_18_24: false, AGE_25_34: true, AGE_35_44: false, AGE_45_54: false, AGE_55_100: false } is not a continuous range
  // E.g. { AGE_13_17: false, AGE_18_24: true, AGE_25_34: true, AGE_35_44: false, AGE_45_54: false, AGE_55_100: false } is a continuous range
  const ageKeys = Object.keys(ageObject)
  let foundTrue = false
  let valid = true

  for (let i = 0; i < ageKeys.length; i++) {
    const key = ageKeys[i]
    const value = ageObject[key]

    if (value) {
      if (!foundTrue) {
        foundTrue = true
      }
    } else if (foundTrue) {
      // We have found a false value after a true value, check if there are any more true values
      const remainingValues = ageKeys.slice(i + 1).map(key => ageObject[key])
      if (remainingValues.includes(true)) {
        valid = false
        break
      }
    }
  }

  return valid
}

export const getCustomAgesIds = values => {
  const customAgeRanges = values[CUSTOM_AGE_RANGES]
  const selectedCustomAgeRanges = Object.keys(customAgeRanges).filter(item => customAgeRanges[item])

  const customAgesIds = []

  selectedCustomAgeRanges.forEach(ageRange => {
    customAgesIds.push(ageRange)
  })

  return customAgesIds
}

export const formatExchangeName = exchangeName => {
  return capitalizeWords(formatStringWithUnderscore(exchangeName)).replace('Exchange', '')
}

export const getValidFormattedExchangeTargetingOptions = dv360ExchangeTargetingOptions => {
  const validExchangeTargetingOptions = dv360ExchangeTargetingOptions.filter(item => item.exchangeDetails?.exchange)
  return validExchangeTargetingOptions.map(item => ({
    label: formatExchangeName(item.exchangeDetails?.exchange),
    value: item.exchangeDetails?.exchange
  }))
}

export const getFormattedOnScreenPositionLabel = value => {
  return capitalizeWords(formatStringWithUnderscore(value.split('ON_SCREEN_POSITION_')[1].toLowerCase()))
}

export const getFormattedContentInstreamPositionLabel = value => {
  return capitalizeWords(formatStringWithUnderscore(value.split('CONTENT_INSTREAM_POSITION_')[1].toLowerCase()))
}

export const getFormattedContentOutstreamPositionLabel = value => {
  return capitalizeWords(formatStringWithUnderscore(value.split('CONTENT_OUTSTREAM_POSITION_')[1].toLowerCase()))
}

export const formatViewabilityLabel = viewability => {
  const viewabilityPercentage = viewability.split('_')[1]
  return viewabilityPercentage === '90'
    ? `${viewabilityPercentage}% or more greater (most viewable)`
    : `${viewabilityPercentage}% or more greater`
}

export const formatDv360ViewabilityTargetingOptions = targetingOptions => {
  return [...targetingOptions].reverse().map(option => {
    // example of string: VIEWABILITY_10_PERCENT_OR_MORE
    const viewability = option.viewabilityDetails.viewability
    return {
      targetingOptionId: option.targetingOptionId,
      label: formatViewabilityLabel(viewability),
      value: viewability
    }
  })
}
